import { FC, useEffect, useMemo, useState } from 'react';

import { useSetRecoilState } from 'recoil';

import DateFilterSearcher from '@service/bugo/components/DateFilterSearcher';
import StatPageTableHeaderBox from '@service/bugo/components/StatPageTableHeaderBox';
import { Image } from '@shared/api/media/media.interface';
import { ShopOrderAgencyAdmin } from '@shared/api/shopOrder/shopOrder.atom';
import { ShopOrderForAgencyAdmin } from '@shared/api/shopOrder/shopOrder.interface';
import { User } from '@shared/api/user/user.interface';
import { Button } from '@shared/components/Button';
import CheckboxFilter, { CheckBoxFilter } from '@shared/components/CheckboxFilter';
import { TextField } from '@shared/components/TextField';
import { DeathCharacter } from '@shared/miscValues';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { THEME } from '@shared/state/atom/theme.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';
import { getAgencyWorkerTeamName } from '@shared/utils/agencyWorkerUtils';
import { formatDate } from '@shared/utils/formatDate';
import { mainBgColorGenerator } from '@shared/utils/mainColorGenerator';
import { Table, TableColumnsType } from 'antd';
import { RangePickerProps } from 'antd/es/date-picker';
import FuzzySearch from 'fuzzy-search';
import _ from 'lodash';
import { CSVLink } from 'react-csv';

import WorkerDetailModalOpener from '../WorkerDetailModalOpener/WorkerDetailModalOpener';
import { DeliveredImageModal } from './DeliveredImageModal';
interface Props {
  shopOrderList: ShopOrderAgencyAdmin[];
  teamTypesFilter: CheckBoxFilter | null;
}

type ShopOrderTableData = {
  id: string;
  index: number;
  shopOrder: ShopOrderForAgencyAdmin;
  shopItemName: string;
  user: Pick<User, '_id' | 'info' | 'bugoAgencyWorkerDetail'>;
  funeralHomeName: string;
  deceasedName: string;
  coffinOutDate: string;
  teamName: string;
  region: string;
  requestAt: Date | '';
  priceWhole: number;
  priceRetail: number;
  deliveredImage?: Image;
};

const getNetPriceWhole = (resultValue: ShopOrderAgencyAdmin[]) =>
  resultValue.reduce((accu, prev) => {
    return accu + prev.orderDetail.shopItemEmbed.priceWhole;
  }, 0);

const getNetPriceAgencyIncome = (resultValue: ShopOrderAgencyAdmin[]) =>
  resultValue.reduce((accu, prev) => {
    return (
      accu +
      (prev.orderDetail.shopItemEmbed.priceRebateAgency ??
        prev.orderDetail.shopItemEmbed.priceRebate)
    );
  }, 0);

export const AgencyAdminShopOrderListTable: FC<Props> =
  function AgencyAdminShopOrderListTable({ shopOrderList, teamTypesFilter }: Props) {
    const customTheme = THEME;
    const [searchValue, setSearchValue] = useState<string>('');

    const [dateRange, setDateRange] = useState<[any | null, any | null]>([null, null]);

    const deliveredImageOpenId = 'agencyAdminShopOrderListTable-deliveredImageOpen';
    const setDeliveredImageOpen = useSetRecoilState(
      simpleOpenAtomFamily(deliveredImageOpenId),
    );
    const [selectShopOrder, setSelectShopOrder] = useState<
      ShopOrderForAgencyAdmin | undefined
    >(undefined);

    //* 페이지네이션 안되있으면 이런애를 씌우면 일단 에러 안남
    const shopOrderPaid = useMemo(() => {
      return shopOrderList;
    }, [shopOrderList]);

    const { userProfile, bugoRole, userBugoBrand } = useAuth();

    const bugoBrand =
      bugoRole() === LowerRole.BugoAgencyBranchAdmin
        ? userBugoBrand
        : userProfile?.bugoAgencyAdminDetail?.bugoBrands[0];

    const [dateFilteredShopOrderList, setDateFilteredShopOrderList] =
      useState<ShopOrderAgencyAdmin[]>(shopOrderPaid);

    const onDateRangeChange: RangePickerProps['onChange'] = (dates) => {
      if (dates) {
        setDateRange(dates);
      } else {
        setDateRange([null, null]);
      }
    };

    // TeamType Filtering
    const [selectedTeamTypes, setSelectedTeamTypes] = useState(teamTypesFilter);

    const teamTypesFilteredFeventList = useMemo(() => {
      if (selectedTeamTypes) {
        return dateFilteredShopOrderList.filter(
          (shopOrder) =>
            selectedTeamTypes[
              shopOrder.fevent?.user?.bugoAgencyWorkerDetail?.teamType ?? ''
            ],
        );
      } else {
        return dateFilteredShopOrderList;
      }
    }, [dateFilteredShopOrderList, selectedTeamTypes]);

    const resultValue = useMemo(() => {
      const searcher = new FuzzySearch(teamTypesFilteredFeventList, [
        'orderDetail.shopItemEmbed.name',
        'fevent.registerNumber',
        'fevent.user.info.name',
        'fevent.user.bugoAgencyWorkerDetail.teamName',
        'fevent.user.bugoAgencyWorkerDetail.teamType',
        'fevent.user.bugoAgencyWorkerDetail.region',
      ]);
      return searcher.search(searchValue);
    }, [teamTypesFilteredFeventList, searchValue]);

    //* dateRange 변경 적용
    useEffect(() => {
      const rangeFilteredList = shopOrderPaid.filter((shopOrder) => {
        if (dateRange[0] && dateRange[1]) {
          if (shopOrder.fevent?.deceasedInfo.coffinOut.date) {
            const coffinOut = new Date(shopOrder.fevent.deceasedInfo.coffinOut.date);
            if (
              dateRange[0].toDate() <= coffinOut &&
              dateRange[1].toDate() >= coffinOut
            ) {
              return true;
            } else {
              return false;
            }
          } else {
            return false;
          }
        } else {
          return true;
        }
      });
      setDateFilteredShopOrderList(rangeFilteredList);
    }, [dateRange, shopOrderPaid]);

    const columns: TableColumnsType<ShopOrderTableData> = [
      {
        title: 'index',
        dataIndex: 'index',
        key: 'index',
        align: 'center',
        render: (index) => {
          return <p className="font-medium">{index}</p>;
        },
      },
      {
        title: '소속',
        dataIndex: 'teamName',
        key: 'teamName',
        align: 'center',
        render: (teamName) => {
          return <p className="font-medium">{teamName}</p>;
        },
      },
      {
        title: '소속지역',
        dataIndex: 'region',
        key: 'region',
        align: 'center',
        render: (region) => {
          return <p className="font-medium">{region}</p>;
        },
      },
      {
        title: '지도사(부고발송)',
        dataIndex: 'user',
        key: 'user',
        align: 'center',
        render: (user) => {
          return (
            <WorkerDetailModalOpener
              workerName={user.info.name}
              workerId={user._id}
              fontSize={14}
            />
          );
        },
      },
      {
        title: '장례식장',
        dataIndex: 'funeralHomeName',
        key: 'funeralHomeName',
        align: 'center',
      },
      {
        title: '고인명',
        dataIndex: 'deceasedName',
        key: 'deceasedName',
        align: 'center',
        render: (deceasedName) => {
          return (
            <p>
              {DeathCharacter}&nbsp;
              {deceasedName}님
            </p>
          );
        },
      },
      {
        title: '발인일',
        dataIndex: 'coffinOutDate',
        key: 'coffinOutDate',
        align: 'center',
      },
      {
        title: '상품명',
        dataIndex: 'shopItemName',
        key: 'name',
        align: 'center',
        render: (name) => {
          return <p className="font-medium">{name}</p>;
        },
      },
      {
        title: '주문일',
        dataIndex: 'requestAt',
        key: 'requestAt',
        align: 'center',

        render: (value: Date | '') => {
          return (
            <p>
              {value
                ? formatDate(value, {
                    contains: {
                      year: false,
                      month: true,
                      date: true,
                      day: true,
                      hours: false,
                      minutes: false,
                    },
                  })
                : ''}
            </p>
          );
        },
      },
      {
        title: '주문시각',
        dataIndex: 'requestAt',
        key: 'requestAt',
        align: 'center',

        render: (value: Date | '') => {
          return (
            <p>
              {value
                ? formatDate(value, {
                    contains: {
                      year: false,
                      month: false,
                      date: false,
                      day: false,
                      hours: true,
                      minutes: true,
                    },
                  })
                : ''}
            </p>
          );
        },
      },
      {
        title: '화환원가',
        dataIndex: 'priceWhole',
        key: 'priceWhole',
        align: 'right',
        render: (value: number) => {
          return <p className="text-gray-600">{value.toLocaleString()}원</p>;
        },
      },

      {
        title: '결제금액',
        dataIndex: 'priceRetail',
        key: 'priceRetail',
        align: 'right',
        render: (value: number) => {
          return <p className="text-gray-600">{value.toLocaleString()}원</p>;
        },
      },
      {
        title: '사진보기',
        dataIndex: 'deliveredImage',
        key: 'deliveredImage',
        align: 'center',
        render: (deliveredImage, record) => {
          if (deliveredImage) {
            return (
              <div className="center-box">
                <Button
                  className="shadow-sm"
                  onClick={() => {
                    setSelectShopOrder(record.shopOrder);
                    setDeliveredImageOpen(true);
                  }}
                >
                  사진보기
                </Button>
              </div>
            );
          } else {
            return <p>사진준비중</p>;
          }
        },
      },
    ];

    const tableData: ShopOrderTableData[] = useMemo(() => {
      return _.map(
        resultValue as ShopOrderForAgencyAdmin[] | null,
        (shopOrder, index): ShopOrderTableData => {
          return {
            index: resultValue.length - index,
            id: shopOrder._id,
            shopOrder: shopOrder,
            deliveredImage: shopOrder.deliveryDetail.deliveredImage,
            shopItemName: shopOrder.orderDetail.shopItemEmbed.name,
            teamName: getAgencyWorkerTeamName(shopOrder.fevent?.user),
            region: shopOrder.fevent?.user.bugoAgencyWorkerDetail?.region ?? '',
            user: shopOrder.fevent?.user,
            funeralHomeName: shopOrder?.funeralHomeInfo?.name,
            deceasedName: shopOrder.fevent?.deceasedInfo.name,
            coffinOutDate: formatDate(shopOrder.fevent?.deceasedInfo.coffinOut.date, {
              contains: {
                year: false,
                month: true,
                date: true,
                day: true,
                hours: false,
                minutes: false,
              },
            }),
            requestAt: shopOrder.paymentDetail.requestedAt ?? '',
            priceWhole: shopOrder.orderDetail.shopItemEmbed.priceWhole,
            priceRetail: shopOrder.orderDetail.shopItemEmbed.priceRetail,
          };
        },
      );
    }, [resultValue]);

    const csvTableData = tableData.map((item) => {
      return {
        소속: item.teamName,
        소속지역: item.region,
        지도사: item.user.info.name,
        장례식장: item.funeralHomeName,
        고인명: item.deceasedName,
        발인일: item.coffinOutDate,
        상품명: item.shopItemName,
        주문일: item.requestAt
          ? formatDate(item.requestAt, {
              contains: {
                year: false,
                month: true,
                date: true,
                day: true,
                hours: false,
                minutes: false,
              },
            })
          : '',
        주문시각: item.requestAt
          ? formatDate(item.requestAt, {
              contains: {
                year: false,
                month: false,
                date: false,
                day: false,
                hours: true,
                minutes: true,
              },
            })
          : '',
        화환원가: item.priceWhole,
        결제금액: item.priceRetail,
      };
    });

    const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(event.currentTarget.value);
    };

    return (
      <div className="space-y-4">
        <div className="flex gap-8">
          <StatPageTableHeaderBox
            left="총 원가"
            right={getNetPriceWhole(resultValue).toLocaleString() + '원'}
          />
          <StatPageTableHeaderBox
            left={`${
              bugoRole() === LowerRole.BugoAgencyBranchAdmin
                ? userProfile?.bugoAgencyBranchAdminDetail?.teamName
                : bugoBrand?.bugoAgency?.nickName
            } 이익금`}
            right={getNetPriceAgencyIncome(resultValue).toLocaleString() + '원'}
          />

          <StatPageTableHeaderBox
            left="화환 주문량"
            right={resultValue.length.toLocaleString() + '건'}
          />
        </div>
        <div className="rounded-lg bg-white px-4">
          <div className="flex w-full items-center justify-between py-4">
            <div className="flex items-center justify-start gap-2">
              <p className="font-medium text-gray-600">
                총{' '}
                <span className="font-bold text-black">
                  {resultValue.length.toLocaleString()}
                </span>
                건
              </p>
              <div className="flex items-center justify-start gap-2">
                <TextField
                  className="border-gray-500"
                  type={'search'}
                  onChange={onChangeHandler}
                  placeholder="검색 (지도사 정보, 회원번호, 상품명)"
                />
              </div>
            </div>
            <DateFilterSearcher
              dateRange={dateRange}
              onChange={onDateRangeChange}
              size="large"
            />
            {/* 소속 필터 */}
            {selectedTeamTypes && (
              <div className="flex items-center space-x-4 border text-sm theme-bg-1 theme-border-1">
                <div
                  className={`center-box self-stretch bg-opacity-40 px-2 font-bold ${mainBgColorGenerator(
                    customTheme,
                  )}`}
                >
                  소속
                </div>
                <div className="flex flex-1 items-center justify-around space-x-4 py-1 pr-2">
                  <CheckboxFilter
                    filter={selectedTeamTypes}
                    setFilter={(checked) => setSelectedTeamTypes(checked)}
                  />
                </div>
              </div>
            )}
            <CSVLink
              filename={'Expense_Table.csv'}
              data={_.flatMapDeep([csvTableData])}
              className="center-box filled-gray-800 rounded-md px-4 py-2 font-bold"
            >
              <p className="text-white">엑셀 다운로드</p>
            </CSVLink>
          </div>
          <Table
            className="w-full"
            columns={columns}
            dataSource={_.flatMapDeep([tableData])}
            pagination={{
              position: ['bottomCenter'],
              defaultPageSize: 15,
              showSizeChanger: true,
              pageSizeOptions: [10, 15, 20, 50, 100],
            }}
            rowKey={'id'}
            bordered
            size={'small'}
          />
          <DeliveredImageModal
            openId={deliveredImageOpenId}
            shopOrder={selectShopOrder}
          ></DeliveredImageModal>
        </div>
      </div>
    );
  };
